import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

/**
 * Ce que l'IA consomme et ce que ça coûte. Les compteurs viennent de la façade IA du serveur
 * (`services/ai/consommation.js`), un par tentative auprès d'un fournisseur ; le coût est calculé
 * par le serveur avec la grille de prix ci-dessous — cet écran ne recalcule rien.
 */

const formatJour = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
const nombre = (n) => Math.round(n || 0).toLocaleString('fr-FR');
const SYMBOLE = { EUR: '€', USD: '$' };

function montant(valeur, devise = 'EUR') {
  const v = valeur || 0;
  // Un appel de modération coûte quelques millièmes de centime : en dessous d'un centime, plus de décimales.
  const decimales = v !== 0 && Math.abs(v) < 0.01 ? 4 : 2;
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })} ${SYMBOLE[devise] || devise}`;
}

/** « 12,30 € + 0,07 $ » : les devises ne s'additionnent pas. */
function couts(c) {
  const parties = Object.entries(c || {}).filter(([, v]) => v > 0).map(([d, v]) => montant(v, d));
  return parties.length ? parties.join(' + ') : montant(0);
}

function Chiffre({ titre, valeur, detail }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{titre}</Typography>
        <Typography variant="h5">{valeur}</Typography>
        {detail ? <Typography variant="body2" color="text.secondary">{detail}</Typography> : null}
      </CardContent>
    </Card>
  );
}

const TARIF_VIDE = { fournisseur: '', modele: '', valableDepuis: '', prixEntreeMillion: '', prixSortieMillion: '', devise: 'EUR', source: '' };

export default function ConsommationIAScreen() {
  const [jours, setJours] = useState(30);
  const [rapport, setRapport] = useState(null);
  const [tarifs, setTarifs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [toutesRaisons, setToutesRaisons] = useState(false);
  const [tarif, setTarif] = useState(TARIF_VIDE);
  const [enregistrement, setEnregistrement] = useState({ enCours: false, message: null, gravite: 'success' });

  const charger = useCallback(() => {
    setChargement(true);
    return Promise.all([AdminService.getConsommationIA(jours), AdminService.getTarifsIA()])
      .then(([r, t]) => { setRapport(r); setTarifs(t); setErreur(null); })
      .catch((err) => setErreur(err.response?.data?.error || 'Impossible de charger la consommation.'))
      .finally(() => setChargement(false));
  }, [jours]);

  useEffect(() => { charger(); }, [charger]);

  const saisirTarif = (fournisseur, modele) => {
    setTarif({ ...TARIF_VIDE, fournisseur, modele, valableDepuis: rapport?.depuis || '' });
    document.getElementById('grille-de-prix')?.scrollIntoView({ behavior: 'smooth' });
  };

  const poserTarif = async (e) => {
    e.preventDefault();
    setEnregistrement({ enCours: true, message: null, gravite: 'success' });
    try {
      await AdminService.poserTarifIA(tarif);
      setTarif(TARIF_VIDE);
      setEnregistrement({ enCours: false, message: 'Tarif enregistré.', gravite: 'success' });
      charger();
    } catch (err) {
      setEnregistrement({ enCours: false, message: err.response?.data?.error || 'Enregistrement impossible.', gravite: 'error' });
    }
  };

  const total = rapport?.total;
  const serie = (rapport?.parJour || []).map((j) => ({ jour: j.jour, EUR: j.couts.EUR, USD: j.couts.USD, appels: j.appels }));
  const aDesDollars = serie.some((j) => j.USD > 0);
  const raisons = rapport?.parRaison || [];

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Consommation IA</Typography>
        <ToggleButtonGroup size="small" exclusive value={jours} onChange={(_, v) => v && setJours(v)}>
          {[7, 30, 90].map((j) => <ToggleButton key={j} value={j}>{j} jours</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Chaque tentative auprès d'un fournisseur est comptée par le serveur, repli compris, avec les jetons que
        sa réponse déclare. Le coût applique la grille de prix (HT) au jour de l'appel. Les scripts lancés à la
        main ne sont pas comptés.
      </Typography>

      {erreur ? <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert> : null}
      {chargement && !rapport ? <CircularProgress /> : null}

      {rapport ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Coût"
                valeur={couts(total.couts)}
                detail={total.appelsSansTarif ? `hors ${nombre(total.appelsSansTarif)} appels sans tarif` : 'tous les appels ont un tarif'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Appels"
                valeur={nombre(total.appels)}
                detail={`dont ${nombre(total.appelsEnRepli)} en repli (${total.appels ? Math.round((100 * total.appelsEnRepli) / total.appels) : 0} %)`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Jetons"
                valeur={`${nombre(total.jetonsEntree)} → ${nombre(total.jetonsSortie)}`}
                detail={`entrée → sortie${total.jetonsRaisonnement ? `, dont ${nombre(total.jetonsRaisonnement)} de réflexion` : ''}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Chiffre
                titre="Échecs"
                valeur={nombre(total.echecs)}
                detail={total.sansMesure ? `${nombre(total.sansMesure)} appels aux jetons estimés ou absents` : 'tous les jetons sont déclarés'}
              />
            </Grid>
          </Grid>

          {rapport.sansTarif.length ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Modèles sans tarif, dont le coût n'est pas compté :{' '}
              {rapport.sansTarif.map(({ fournisseur, modele }) => (
                <Button key={`${fournisseur}|${modele}`} size="small" onClick={() => saisirTarif(fournisseur, modele)}>
                  {fournisseur} · {modele}
                </Button>
              ))}
            </Alert>
          ) : null}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title="Coût par jour" />
            <CardContent sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="jour" tickFormatter={formatJour} />
                  <YAxis />
                  <RechartsTooltip
                    labelFormatter={formatJour}
                    formatter={(v, nom) => [montant(v, nom), nom]}
                  />
                  <Legend />
                  <Bar dataKey="EUR" name="EUR" fill="#1976d2" />
                  {aDesDollars ? <Bar dataKey="USD" name="USD" fill="#ed6c02" /> : null}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title="Par modèle" />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fournisseur</TableCell>
                    <TableCell>Modèle</TableCell>
                    <TableCell align="right">Appels</TableCell>
                    <TableCell align="right">En repli</TableCell>
                    <TableCell align="right">Échecs</TableCell>
                    <TableCell align="right">Jetons entrée</TableCell>
                    <TableCell align="right">Jetons sortie</TableCell>
                    <TableCell align="right">Coût</TableCell>
                    <TableCell>Tarif (par million)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rapport.parModele.map((m) => (
                    <TableRow key={`${m.fournisseur}|${m.modele}`}>
                      <TableCell>{m.fournisseur}</TableCell>
                      <TableCell>{m.modele}</TableCell>
                      <TableCell align="right">{nombre(m.appels)}</TableCell>
                      <TableCell align="right">{nombre(m.appelsEnRepli)}</TableCell>
                      <TableCell align="right">{nombre(m.echecs)}</TableCell>
                      <TableCell align="right">{nombre(m.jetonsEntree)}</TableCell>
                      <TableCell align="right">{nombre(m.jetonsSortie)}</TableCell>
                      <TableCell align="right">{m.tarif ? couts(m.couts) : '—'}</TableCell>
                      <TableCell>
                        {m.tarif
                          ? `${montant(m.tarif.prixEntreeMillion, m.tarif.devise)} / ${montant(m.tarif.prixSortieMillion, m.tarif.devise)}`
                          : <Button size="small" onClick={() => saisirTarif(m.fournisseur, m.modele)}>Saisir</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!rapport.parModele.length ? (
                    <TableRow><TableCell colSpan={9}>Aucun appel sur la période.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader
              title="Par raison"
              subheader="La route qui a demandé l'appel, ou la tâche planifiée ; l'objet dit ce que l'appel faisait."
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Raison</TableCell>
                    <TableCell>Objet</TableCell>
                    <TableCell>Capacité</TableCell>
                    <TableCell align="right">Appels</TableCell>
                    <TableCell align="right">Jetons entrée</TableCell>
                    <TableCell align="right">Jetons sortie</TableCell>
                    <TableCell align="right">Durée moyenne</TableCell>
                    <TableCell align="right">Coût</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(toutesRaisons ? raisons : raisons.slice(0, 25)).map((r) => (
                    <TableRow key={`${r.raison}|${r.objet}|${r.capacite}`}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.raison}</TableCell>
                      <TableCell>{r.objet}</TableCell>
                      <TableCell>{r.capacite}</TableCell>
                      <TableCell align="right">{nombre(r.appels)}</TableCell>
                      <TableCell align="right">{nombre(r.jetonsEntree)}</TableCell>
                      <TableCell align="right">{nombre(r.jetonsSortie)}</TableCell>
                      <TableCell align="right">
                        {r.appels + r.echecs ? `${(r.dureeMs / (r.appels + r.echecs) / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s` : '—'}
                      </TableCell>
                      <TableCell align="right">{couts(r.couts)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {raisons.length > 25 ? (
              <Box sx={{ p: 1 }}>
                <Button size="small" onClick={() => setToutesRaisons((v) => !v)}>
                  {toutesRaisons ? 'Afficher les 25 premières' : `Afficher les ${raisons.length}`}
                </Button>
              </Box>
            ) : null}
          </Card>
        </>
      ) : null}

      <Card variant="outlined" id="grille-de-prix">
        <CardHeader
          title="Grille de prix"
          subheader="Prix HT par million de jetons. Un nouveau prix s'ajoute avec sa date de début : les jours passés gardent l'ancien."
        />
        <CardContent>
          <Box component="form" onSubmit={poserTarif} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField size="small" label="Fournisseur" required value={tarif.fournisseur}
              onChange={(e) => setTarif({ ...tarif, fournisseur: e.target.value })} />
            <TextField size="small" label="Modèle" required value={tarif.modele} sx={{ minWidth: 260 }}
              onChange={(e) => setTarif({ ...tarif, modele: e.target.value })} />
            <TextField size="small" label="Valable depuis" type="date" required InputLabelProps={{ shrink: true }}
              value={tarif.valableDepuis} onChange={(e) => setTarif({ ...tarif, valableDepuis: e.target.value })} />
            <TextField size="small" label="Entrée / M" type="number" required inputProps={{ min: 0, step: 'any' }}
              value={tarif.prixEntreeMillion} onChange={(e) => setTarif({ ...tarif, prixEntreeMillion: e.target.value })} />
            <TextField size="small" label="Sortie / M" type="number" required inputProps={{ min: 0, step: 'any' }}
              value={tarif.prixSortieMillion} onChange={(e) => setTarif({ ...tarif, prixSortieMillion: e.target.value })} />
            <TextField size="small" label="Devise" select value={tarif.devise}
              onChange={(e) => setTarif({ ...tarif, devise: e.target.value })}>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <TextField size="small" label="Source (page de prix)" value={tarif.source} sx={{ flex: 1, minWidth: 220 }}
              onChange={(e) => setTarif({ ...tarif, source: e.target.value })} />
            <Button type="submit" variant="contained" disabled={enregistrement.enCours}>Enregistrer</Button>
          </Box>
          {enregistrement.message ? <Alert severity={enregistrement.gravite} sx={{ mb: 2 }}>{enregistrement.message}</Alert> : null}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell>Modèle</TableCell>
                  <TableCell>Depuis</TableCell>
                  <TableCell align="right">Entrée / M</TableCell>
                  <TableCell align="right">Sortie / M</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tarifs.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.fournisseur}</TableCell>
                    <TableCell>{t.modele}</TableCell>
                    <TableCell>{new Date(`${t.valableDepuis}T12:00:00`).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell align="right">{montant(t.prixEntreeMillion, t.devise)}</TableCell>
                    <TableCell align="right">{montant(t.prixSortieMillion, t.devise)}</TableCell>
                    <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.source || ''}>
                      {t.source || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
